import moment from 'moment'
import { ActionType, ReferralStatus } from '@/api/types'

/**
 * Overdue tasks on the dashboard: open tasks dated before today, on leads that are still open.
 * The dashboard shows the last OVERDUE_DAYS days and loads older ones on request.
 */
export const OVERDUE_DAYS = 30

const FINISHED_REFERRAL_STATUSES: string[] = [ReferralStatus.SUCCESSFUL, ReferralStatus.CLOSED]

export interface OverdueCandidate {
    type: string
    completed: boolean
    actionDate: Date|string
    referral: { status: string }|null
}

export function overdueWindow(now: Date = new Date()): { after: Date, strictlyBefore: Date } {
    const today = moment(now).startOf('day')

    return {
        after: today.clone().subtract(OVERDUE_DAYS, 'days').toDate(),
        strictlyBefore: today.toDate(),
    }
}

/**
 * A Date as the local calendar day ('YYYY-MM-DD'). keyvue-api reads that as midnight in its own timezone
 * (Europe/Berlin, the same as ours), whereas a Date object goes out as UTC and shifts the cut-off by an hour or two.
 */
export function apiDate(date: Date): string {
    return moment(date).format('YYYY-MM-DD')
}

export function isOverdueTask(action: OverdueCandidate, now: Date = new Date()): boolean {
    if (action.type === ActionType.MEETING || action.completed || !action.referral) {
        return false
    }

    if (FINISHED_REFERRAL_STATUSES.includes(action.referral.status)) {
        return false
    }

    return moment(action.actionDate).isBefore(moment(now).startOf('day'))
}

export function groupByDay<T extends OverdueCandidate>(actions: T[]): { [date: string]: T[] } {
    const byDay: { [date: string]: T[] } = {}

    actions
        .slice()
        .sort((a, b) => moment(a.actionDate).valueOf() - moment(b.actionDate).valueOf())
        .forEach(action => {
            const day = moment(action.actionDate).format('YYYY-MM-DD')
            if (!(day in byDay)) {
                byDay[day] = []
            }
            byDay[day].push(action)
        })

    return byDay
}

/** A task (not an appointment) that is completed: shown with a tick in the agenda */
export function isDoneTask(action: OverdueCandidate): boolean {
    return action.type !== ActionType.MEETING && action.completed
}
